// C:\Users\cornn\source\repos\purehealthnexus\src\components\AiDashboard\AiMyNutritionStatus\AiUser\AiUserSettings\AiUserSettingConst.jsx
// Static configuration + helpers for AiUserSettings (no JSX).

export const HEIGHT_UNITS = {
  METRIC: "cm",
  IMPERIAL: "imperial",
};

export const WEIGHT_UNITS = {
  METRIC: "kg",
  IMPERIAL: "lb",
};

export const ADVANCED_ACTIVITY_NONE_ID = "none";

// Sex options
export const sexOptions = [
  { id: "male", label: "Male" },
  { id: "female", label: "Female" },
];

// Daily physical activity options
export const physicalActivityOptions = [
  {
    id: "low",
    label: "Less than 30 min/day of moderate activity",
    helperText: "Mostly sitting or light movement during the day.",
  },
  {
    id: "moderate",
    label: "30-60 min/day of moderate activity",
    helperText: "Regular walking or equivalent most days.",
  },
  {
    id: "high",
    label: "More than 60 min/day of moderate activity",
    helperText: "Highly active lifestyle or frequent training.",
  },
];

// Advanced activity options for athletes / structured training
export const advancedActivityTypeOptions = [
  {
    id: ADVANCED_ACTIVITY_NONE_ID,
    label: "No additional structured training",
    example: "Only your normal daily movement.",
  },
  {
    id: "walking",
    label: "Walking / easy cardio",
    example: "For example, 30-60 minutes brisk walking.",
  },
  {
    id: "running",
    label: "Running / jogging",
    example: "For example, 30-60 minutes steady running.",
  },
  {
    id: "hiit",
    label: "HIIT / interval training",
    example: "For example, 20-40 minutes high intensity intervals.",
  },
  {
    id: "strength",
    label: "Strength / resistance training",
    example: "For example, 30-60 minutes weights or bodyweight exercises.",
  },
  {
    id: "other",
    label: "Other sport or training",
    example: "For example, cycling, team sports, or cross-training.",
  },
];

// Meal preferences: Diet style options (mutually exclusive)
export const mealPrefDietStyleOptions = [
  {
    id: "none",
    label: "No specific diet",
    description: "Include all regular meals.",
  },
  {
    id: "vegetarian",
    label: "Vegetarian (no meat or fish)",
    description:
      "Includes eggs and dairy unless you also select those restrictions below.",
  },
  {
    id: "vegan",
    label: "Vegan (no animal products)",
    description:
      "Excludes meat, fish, eggs, dairy, and other animal products.",
  },
  {
    id: "pescatarian",
    label: "Pescatarian (fish, no meat)",
    description: "Includes fish and seafood, no poultry or red meat.",
  },
  {
    id: "noRedMeat",
    label: "No red meat",
    description: "Avoids beef, lamb, pork. Poultry and fish are OK.",
  },
];

// Meal preferences: Allergies & intolerances (multi-select)
export const mealPrefAllergyOptions = [
  { id: "glutenFree", label: "Gluten-free" },
  { id: "dairyFree", label: "Dairy-free / lactose-free" },
  { id: "nutFree", label: "Nut-free (peanuts & tree nuts)" },
  { id: "eggFree", label: "Egg-free" },
  { id: "soyFree", label: "Soy-free" },
  { id: "shellfishFree", label: "Shellfish-free" },
  { id: "fishFree", label: "Fish-free" },
  { id: "sesameFree", label: "Sesame-free" },
];

// Meal preferences: Religious & ethical restrictions (multi-select)
export const mealPrefReligiousOptions = [
  { id: "noPork", label: "No pork" },
  { id: "noBeef", label: "No beef" },
  { id: "noAlcohol", label: "No alcohol in recipes" },
  {
    id: "halalFriendly",
    label: "Halal-friendly preference",
    description:
      "Avoids pork and alcohol. Does not guarantee formal halal certification.",
  },
  {
    id: "kosherStyle",
    label: "Kosher-style preference",
    description:
      "Avoids pork and shellfish; does not guarantee formal kosher certification.",
  },
];

// Meal preferences: Ingredient dislikes (multi-select)
export const mealPrefDislikeOptions = [
  { id: "cilantro", label: "Coriander / cilantro" },
  { id: "mushrooms", label: "Mushrooms" },
  { id: "olives", label: "Olives" },
  { id: "spicy", label: "Very spicy foods (chili/peppers)" },
  { id: "seafoodFlavor", label: "Seafood flavor" },
];

// Meal preferences: Cooking style & effort (multi-select)
export const mealPrefConvenienceOptions = [
  { id: "quickMeals", label: "Quick meals (≈ 15–30 min)" },
  { id: "onePot", label: "One-pot / one-pan meals" },
  { id: "batchCooking", label: "Batch-cooking friendly (good for leftovers)" },
  {
    id: "minimalEquipment",
    label: "Minimal equipment (no blender, food processor, etc.)",
  },
];

// Labels used in the validation summary
export const FIELD_LABELS = {
  birthDateIso: "Date of birth",
  hasAcknowledgedBirthDate: "Date of birth confirmation",
  sex: "Sex",
  height: "Height",
  weight: "Weight",
  physicalActivityLevelId: "Daily activity level",
  advancedActivityTypeId: "Main training type",
  advancedActivityMinutesPerDay: "Training minutes per day",

  // New weight-loss goal fields (mapped to Firestore keys)
  weightLoss: "Weight loss focus",
  looseWeight: "Weight loss goal (kg)",
};

export const BUTTON_LABELS = {
  saveProfile: "Save profile",
  saveChanges: "Save changes",
  saving: "Saving...",
  cancel: "Cancel",
};

export const VALIDATION_MESSAGES = {
  birthDateIso: "Date of birth is required.",
  hasAcknowledgedBirthDate: "Please confirm your date of birth.",
  sex: "Sex is required.",
  height: "Height is required.",
  weight: "Weight is required.",
  physicalActivityLevelId: "Daily activity level is required.",
  advancedActivityTypeId: "Main training type is required.",
  advancedActivityMinutesPerDay: "Training minutes per day is required.",

  // Weight-loss is optional overall; if enabled, looseWeight should be present.
  looseWeight:
    "Please enter how many kilograms you would like to lose (for example 5 or 10).",
};

export const UI_COPY = {
  sheetTitle: "Profile settings",
  birthDate: {
    label: "Date of birth",
    helper: "Used to calculate age-specific nutrition ranges and guidance.",
    acknowledgement:
      "I confirm my date of birth is correct. An incorrect date may result in untailored nutrition.",
  },
  sex: {
    label: "Sex:",
    helper:
      "Only used to estimate physiological nutrition needs and reference ranges.",
  },
  height: {
    label: "Height",
    toggleAriaLabel: "Toggle height unit between centimeters and feet/inches",
    toggleLabels: {
      [HEIGHT_UNITS.METRIC]: "ft",
      [HEIGHT_UNITS.IMPERIAL]: "cm",
    },
    unitLabels: {
      [HEIGHT_UNITS.METRIC]: "cm",
      imperialFeet: "ft:",
      imperialInches: "in:",
    },
  },
  weight: {
    label: "Weight",
    toggleAriaLabel: "Toggle weight unit between kilograms and pounds",
    toggleLabels: {
      [WEIGHT_UNITS.METRIC]: "lb",
      [WEIGHT_UNITS.IMPERIAL]: "kg",
    },
    unitLabels: {
      [WEIGHT_UNITS.METRIC]: "kg",
      [WEIGHT_UNITS.IMPERIAL]: "lb",
    },
    savingCopy: {
      [WEIGHT_UNITS.METRIC]: "You are saving to kilograms (kg).",
      [WEIGHT_UNITS.IMPERIAL]: "You are saving to pounds (lb).",
    },
  },

  // Weight loss new div with fields (config only; JSX uses these keys)
  weightLoss: {
    sectionLabel: "Weight loss focus (optional)",
    helper:
      "Set a simple weight loss goal so your BMI tile can build a personalised plan. You can change or turn this off at any time.",
    toggle: {
      label: "I want to focus on losing weight right now",
      ariaLabel: "Toggle weight loss focus on or off",
      onCopy: "Weight loss focus is on.",
      offCopy: "Weight loss focus is off.",
    },
    goal: {
      label: "How many kilograms would you like to lose?",
      placeholder: "e.g. 5 or 10",
      unitLabel: "kg",
      helper:
        "Smaller, realistic goals (for example 5–10 kg) are usually easier to maintain. This is used to calculate your BMI plan only.",
      minRecommendedKg: 2,
      maxRecommendedKg: 50,
    },
    info: {
      bmiTileHint:
        "When this is on, your BMI tile will show a goal, target weight, safe pace and an indicative kcal/day target.",
      safetyHint:
        "This is not a medical prescription. If you have a medical condition or are underweight, discuss goals with a healthcare professional.",
    },
  },

  activity: {
    label: "Daily activity level",
    helper: "Helps estimate how many calories you burn in a day.",
    advancedToggle: {
      prefix: "Advanced mode:",
      on: "On",
      off: "Off",
      ariaLabel: "Toggle advanced mode for structured training",
    },
    clearButton: {
      label: "Clear training",
      ariaLabel: "Clear structured training",
      title: "Clear structured training",
    },
    baselineLabel: "Overall movement on a typical day",
    baselinePlaceholder: "Choose activity level...",
    advancedIntro:
      "Optional: add your structured training to refine athlete-level calorie estimates.",
    advancedLabel: "Main training type (most days)",
    advancedPlaceholder: "Choose training type...",
    minutesLabel:
      "Approximate minutes of this training per day (average)",
    minutesUnitLabel: "min/day",
  },
  mealPreferences: {
    label: "Meal & ingredient preferences",
    helper:
      "Used to filter out meals that don't fit your diet, allergies, or key preferences.",
    triggerPlaceholder: "Choose meal preferences…",
    dietStyleHeader: "Diet style (choose one that fits best)",
    allergiesHeader: "Allergies & intolerances",
    allergiesHelper:
      "We'll hide meals that contain these ingredients. Always double-check labels if you have a severe allergy.",
    otherAllergyLabel: "Other allergy or intolerance",
    otherAllergyPlaceholder:
      "e.g. kiwi, certain fruits, specific additives",
    religiousHeader: "Religious & ethical restrictions",
    religiousHelper:
      "We'll avoid meals that clearly conflict with these choices.",
    dislikesHeader: "Ingredients I really dislike",
    dislikesHelper:
      "We'll deprioritize meals that rely on these ingredients.",
    otherDislikesLabel: "Other ingredients I dislike",
    otherDislikesPlaceholder:
      "e.g. blue cheese, onions, pickles",
    convenienceHeader: "Cooking style & effort",
    convenienceHelper:
      "Helps us choose meals that fit your time and kitchen setup.",
  },
  validationSummary: {
    title: "Please complete the highlighted fields before saving:",
  },
  footer:
    "These details are stored once and only updated occasionally to keep your nutrition guidance tailored.",
  accountLinks: {
    separator: " | ",
    changePlan: {
      label: "Change Plan",
      href: "/home",
    },
    billing: {
      label: "Billing",
      href: "/account/billing",
    },
    deleteAccount: {
      label: "Delete Account",
      href: "/delete-account",
    },
  },
};

export const ERROR_MESSAGES = {
  setProfileComplete: "Failed to set profileSetUpComplete",
  saveProfile: "Failed to save extended profile to Firestore",
  missingUser: "AiUserSettings: no currentUser when saving profile",
};

export const ACTIVITY_LEVEL_MAP = {
  low: 1,
  moderate: 2,
  high: 3,
};

export const FIRESTORE_COLLECTIONS = {
  users: "users",
};

// Unit conversion helpers (metric → imperial and vice versa)
export function convertFeetAndInchesToCentimeters(feetText, inchesText) {
  const feet = parseFloat(feetText || "0");
  const inches = parseFloat(inchesText || "0");
  const totalInches = feet * 12 + inches;
  if (Number.isNaN(totalInches) || totalInches <= 0) return undefined;
  return totalInches * 2.54;
}

export function convertPoundsToKilograms(poundsText) {
  const pounds = parseFloat(poundsText || "0");
  if (Number.isNaN(pounds) || pounds <= 0) return undefined;
  return pounds * 0.45359237;
}

// NEW: reverse converters for pre-populating edit form
export function centimetersToFeetInches(cm) {
  const n = Number(cm);
  if (!Number.isFinite(n) || n <= 0) return { feet: "", inches: "" };
  const totalInches = Math.round(n / 2.54);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches - feet * 12;
  return { feet: String(feet), inches: String(inches) };
}

export function kilogramsToPounds(kg) {
  const n = Number(kg);
  if (!Number.isFinite(n) || n <= 0) return "";
  return String(Math.round(n / 0.45359237));
}
